/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Pulse › Overview (strategy 9.1, mockup admin-7, "Build as drawn"): five tiles, the helped rate by domain, the top five
 * asked for but not possible, today's digest and the Needs a look counts, from GET /v1/admin/pulse/overview with the date
 * range, domain and surface filters. Export NDJSON downloads the records behind the view.
 */
import { useState } from 'react';
import { CircleSlash, Newspaper } from 'lucide-react';
import { api } from '../../lib/api';
import {
  deltaText, domainLabel, pctText, periodName, pulseExportHref, pulseHref, reasonLabel, subjectName, usdText, type PulseOverview as OverviewData,
} from '../../lib/pulse';
import { linkClick } from '../../shell/router';
import { EmptyState, Grid, Kpi, LinkButton, PageHeader, Panel, Skeleton, Stack, StatusChip, Table, type Column } from '../../ui';
import { count, time } from '../../ui/format';
import { BarRow, DEFAULT_FILTERS, ExportButton, FilterBar, PageLink, PulseError, filterParams, useLoad, type PulseFilters } from './common';

type NotPossibleRow = OverviewData['notPossible'][number];
interface LookRow { key: string; what: string; count: string; note: string; href: string }

function lookRows(data: OverviewData): LookRow[] {
  const look = data.needsALook;
  return [
    { key: 'problems', what: 'Open problems', count: count(look.openProblems), note: look.newProblemsToday > 0 ? `${count(look.newProblemsToday)} new today` : 'none new today', href: '/pulse-problems' },
    { key: 'feedback', what: 'Feedback not yet reviewed', count: count(look.feedbackUnreviewed), note: look.feedbackUndo > 0 ? `${count(look.feedbackUndo)} with "Had to undo it"` : 'none with "Had to undo it"', href: '/pulse-feedback?reviewed=0' },
    { key: 'unknown', what: 'Outcome unknown', count: pctText(look.unknownPct, 1), note: 'judge below the confidence floor', href: '/pulse-asks?outcome=unknown' },
    { key: 'spend', what: 'Pulse spend today', count: usdText(look.spendTodayUsd), note: `of the ${usdText(look.budgetUsd)} cap`, href: '/pulse-settings' },
  ];
}

export function PulseOverview() {
  const [filters, setFilters] = useState<PulseFilters>(DEFAULT_FILTERS);
  const params = filterParams(filters);
  const { load, reload, reloading } = useLoad(() => api.pulseOverview(params), JSON.stringify(params));
  const data = load.state === 'ready' ? load.data : null;
  const loading = load.state === 'loading';

  const notPossibleColumns: Array<Column<NotPossibleRow>> = [
    { key: 'intent', header: 'Intent', cell: (row) => <PageLink href={pulseHref.intent(row.intentKey)}>{row.intent}</PageLink> },
    { key: 'asks', header: 'Asks', numeric: true, cell: (row) => count(row.asks) },
    { key: 'why', header: 'Why', cell: (row) => <StatusChip tone="neutral">{reasonLabel(row.reason)}</StatusChip> },
    { key: 'repeats', header: 'Repeats', numeric: true, hideOnPhone: true, cell: (row) => count(row.repeats) },
  ];
  const lookColumns: Array<Column<LookRow>> = [
    { key: 'what', header: 'What', cell: (row) => <PageLink href={row.href}>{row.what}</PageLink> },
    { key: 'count', header: 'Count', numeric: true, cell: (row) => row.count },
    { key: 'note', header: <span className="ui-sr">Note</span>, muted: true, cell: (row) => row.note },
  ];

  const failing = data?.failingNow ?? [];
  const period = periodName(filters.days);

  return (
    <>
      <PageHeader
        title="Pulse"
        meta={<span>What people asked Gyro, whether it helped, and what to fix. Anonymous: no names, no devices.</span>}
        actions={(
          <>
            <FilterBar filters={filters} onChange={setFilters} />
            <ExportButton href={pulseExportHref('records', params)} />
          </>
        )}
      />
      <div className="ui-page">
        <Stack>
          {load.state === 'failed' ? <PulseError title="The Pulse overview could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /> : null}
          <div className="pulse-kpis" role="group" aria-label="Pulse at a glance">
            <Kpi label="Asks" loading={loading} value={data ? count(data.asks.total) : '—'} sub={data ? <span className="ui-text-ok">{deltaText(data.asks.total, data.asks.previous, period) ?? 'no earlier period to compare'}</span> : undefined} />
            <Kpi label="Helped" loading={loading} value={data ? pctText(data.helped.pct) : '—'} sub="done + done with gaps" />
            <Kpi
              label="Could not" loading={loading} value={data ? pctText(data.couldNot.pct) : '—'}
              sub={data ? <span className="ui-text-warn">{data.couldNot.saidCannotPct === null ? `${count(data.couldNot.count)} asks` : `${pctText(data.couldNot.saidCannotPct)} of it: Gyro said it couldn't`}</span> : undefined}
            />
            <Kpi
              label="Feedback" loading={loading} value={data ? (data.feedback.score === null ? '—' : data.feedback.score.toFixed(1)) : '—'} suffix={data && data.feedback.score !== null ? '/ 5' : undefined}
              sub={data ? `${count(data.feedback.cards)} cards${data.feedback.workedPct === null ? '' : ` · ${pctText(data.feedback.workedPct)} Worked`}` : undefined}
            />
            <Kpi
              label="Failing now" loading={loading} value={data ? count(failing.length) : '—'}
              sub={data ? (failing.length ? <span className="ui-text-bad">{failing.map((item) => subjectName(item.subjectId).name).join(' · ')}</span> : 'every tool and connector is healthy') : undefined}
            />
          </div>

          <Grid cols={2}>
            <Panel title="Helped rate by domain" description="done + done with gaps, of the asks in each domain" actions={<span className="ui-quiet">{filters.days} days</span>}>
              {loading ? (
                <div className="pulse-bars" aria-busy="true">{[0, 1, 2, 3].map((i) => <Skeleton key={i} />)}</div>
              ) : data && data.helpedByDomain.length ? (
                <div className="pulse-bars">
                  {data.helpedByDomain.map((row) => (
                    <BarRow key={row.domain} label={domainLabel(row.domain)} href={pulseHref.domain(row.domain)} value={row.helpedPct / 100} figure={pctText(row.helpedPct)} meterLabel={`${domainLabel(row.domain)}: helped ${pctText(row.helpedPct)} of ${count(row.asks)} asks`} />
                  ))}
                </div>
              ) : (
                <EmptyState compact icon={CircleSlash} title="No asks yet">Each domain's helped rate appears here as people ask Gyro.</EmptyState>
              )}
            </Panel>
            <Panel flush title="Asked for but not possible" description="Intents Gyro could not do, with the reason" actions={<span className="ui-quiet">top {data ? Math.min(5, data.notPossible.length) : 5} · repeats counted</span>}>
              <Table
                label="Asked for but not possible"
                columns={notPossibleColumns}
                rows={(data?.notPossible ?? []).slice(0, 5)}
                rowKey={(row) => row.intentKey}
                loading={loading}
                skeletonRows={5}
                empty={<EmptyState compact icon={CircleSlash} title="Nothing asked for that Gyro could not do">Intents Gyro said it could not do, or tried and failed, appear here with the reason.</EmptyState>}
              />
            </Panel>
          </Grid>

          <Grid cols={2}>
            <Panel
              title="Today's digest"
              description="The analyst's read of the day"
              actions={data?.digest ? <span className="ui-quiet">{time(data.digest.createdAt, { zone: true })} · {data.digest.model}</span> : null}
            >
              {loading ? (
                <div aria-busy="true"><Skeleton size="lg" /><Skeleton /><Skeleton /></div>
              ) : data?.digest ? (
                <div className="ui-form">
                  <div className="pulse-analysis"><p>{data.digest.summary || 'The digest has no summary line.'}</p></div>
                  <LinkButton href={pulseHref.digest(data.digest.id)} icon={Newspaper} onClick={(event) => linkClick(event)}>Open the digest</LinkButton>
                </div>
              ) : (
                <EmptyState compact icon={Newspaper} title="No digest yet">The daily digest is written at the time set under Pulse › Settings; an admin can run it early from Digests.</EmptyState>
              )}
            </Panel>
            <Panel flush title="Needs a look" description="What an admin may want to open next">
              <Table
                label="Needs a look"
                columns={lookColumns}
                rows={data ? lookRows(data) : []}
                rowKey={(row) => row.key}
                loading={loading}
                skeletonRows={4}
              />
            </Panel>
          </Grid>
        </Stack>
      </div>
    </>
  );
}
